import {IPopupable, ORelativePosition} from "/@back@/commons/widgets/popupable.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {isWedletBoxSelection, IWedEditorBoxSel} from "/@back@/edit/wed/features/boxSel.js";
import {isWedDefaultDisplay} from "/@back@/edit/wed/wedCore.js";
import {IWedEditor} from "/@back@/edit/wed/wedEditor.js";
import {OWedEditorBoxConfig} from "/@back@/edit/wed/wedEditorBox.js";
import {EWedletEditMode, findWedEditor, IEditableWedlet, IElementWedlet, IParentWedlet, IS_EltWedlet, IWedlet, IWedletArea, OVisitWedletOptions, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {isWedletSingleElt, IWedletSingleElt} from "/@back@/edit/wed/wedlets/wedletSingleElt.js";
import {ACTION, Action} from "/@lib@/commons/actions.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM, ENodeType, JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IJmlSet, JML} from "/@lib@/commons/xml/jml.js";
import {ISkStructDef} from "/@lib@/edit/schema/schemaAnnots.js";
import {ISkImporter, OSkImportDatas, OSkPasteContext, OSkPasteSearchContext} from "/@lib@/edit/schema/schemaMeta.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {SkRuleElt, SkRuleNode} from "/@lib@/edit/schema/schemaPatterns.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {IS_ActionableEnabled} from "/@back@/commons/actionables.js";
import {XmlBatch} from "/@lib@/edit/ot/xmlHouse.js";
import {IDocBatch} from "/@lib@/edit/docHolder.js";
import {GFX} from "/@lib@/commons/utils/gfx.js";
import {BoxHost} from "/@back@/edit/wed/wedlets/box/boxTags.js";
import {LANG} from "/@lib@/commons/lang.js";

/**
 * Gestion générique de l'insertion/remplacement d'élément :
 * - met à dispo un API pour activer les points d'insertion dans l'éditeur
 * - gère les listeners dragOver pour activation auto au drag.
 */
export interface IWedEditorInsMgr extends IWedEditor {

	/** Démarre une session d'insertion d'un contenu. */
	startInsertSession?(datas: OSkImportDatas, byDrag: boolean, ctrl?: IInsSessionCtrl): void

	/** Stop la session en cours d'insertion d'un contenu et élimine tous les widgets d'insertion. */
	endInsertSession?(): void

	/** Interne. options pour visiter les wedlets dans une InsSession. */
	_insMgrVisitOptions?: OVisitWedletOptions
	/** Interne. Session en cours. */
	_insSession?: InsSession
	/** Interne. origine du 1er dragenter créateur de la session pour filtrer le target au dragleave pour éliminer la session. */
	_dragEnter?: EventTarget

	//WAIT impl Chrome https://bugs.chromium.org/p/chromium/issues/detail?id=388976
	///** Interne. Caret d'insertion dans le texte. */
	//_insCaret?: MarkImportCaret

}


export function AgInsSelEditor(cls: any, visitOptions?: OVisitWedletOptions): any {
	const proto = cls.prototype as IWedEditorInsMgr;
	const superInit = proto.initialize;
	proto.initialize = function (this: IWedEditorBoxSel & IWedEditorInsMgr, config: OWedEditorBoxConfig): IWedEditorBoxSel {
		superInit.call(this, config);

		this.rootNode.addEventListener('delegate-host', (ev: Event) => {
			const elt = ev.composedPath()[0];
			const host = (elt as IElementWedlet).delegatedHost;
			if (host instanceof BoxHost) addDragMgrOnWedletHost(host); //on est bien sur un delegate-host de type Box
		});

		this.rootNode.addEventListener("dragenter", (ev: DragEvent) => {
			if (this._dragEnter) {
				//session de drag déjà en cours
				//on garde ce dernier dragenter sans recalculer.
				this._dragEnter = ev.target;
				return;
			}
			//console.log("dragenter:::", (ev.dataTransfer as any).insSession, ev.target, ev);
			const datas = this.wedMgr.extractDatasFromDragSession(ev.dataTransfer);
			if (datas) {
				this._dragEnter = ev.target;
				this.startInsertSession(datas, true);
			}
		});

		this.rootNode.addEventListener("dragleave", (ev: DragEvent) => {
			if (this._dragEnter !== ev.target) return;
			//console.log("dragleave:::", (ev.dataTransfer as any).insSession, ev.target, ev);

			//on quitte le noeud corrrespondant au dernier dragenter
			// Résoud un pb d'imbrication d'events 'dragenter' et 'dragleave' mal ordonnés sur des noeuds différents
			// autour de la scroll bar notamment.
			this._dragEnter = null;
			this.endInsertSession();
		});

		//TODO abonnement aux updates concurrents du contenu pendant la session pour invalider
		//     et rafraichir les marques d'insertion potentiellement concernées par la modif.
		//     cf IInsMgrWedlet.eraseInsertMarks() et InsSession.redraw()

		//
		const insAction = new ListInsBoxStruct();
		config.reg.addToList("actions:wed:commonbar:start", "insTag", 1, insAction, 60);
		this.wedMgr.accelKeyMgr.addAccelKey((navigator.platform.indexOf("Mac") >= 0 ? "i" : "Insert"), (navigator.platform.indexOf("Mac") >= 0 ? "ctrl" : ""), insAction);

		return this;
	};
	proto._insMgrVisitOptions = visitOptions;
	proto.startInsertSession = startInsertSession;
	proto.endInsertSession = endInsertSession;

	return cls;
}

/** Abonne un container de widgets WED aux drag&drop des widgets. */
export function addDragMgrOnWedletHost(root: HTMLElement | ShadowRoot) {
	//console.log("addDragMgrOnWedletHost:::::", root);
	root.addEventListener("dragstart", function onDragStart(this: void, ev: DragEvent) {
		//console.log("onDragStart::::", ev.target, ev.composedPath());
		const eltWedlet = DOM.findParentOrSelf(ev.target as Element, null, IS_EltWedlet);
		const wedlet = eltWedlet ? eltWedlet.wedlet : null;
		const wedMgr = wedlet.wedMgr;
		const boxSelMgr = (wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
		if (!eltWedlet || wedlet.isVirtual() || (boxSelMgr && boxSelMgr.activeWedlet !== wedlet)) {
			ev.preventDefault();
			return;
		}
		const doc = wedMgr.docHolder.getDocument();
		const frag = doc.createElementNS(DOM.SCCORE_NS, "fragment");
		if (boxSelMgr) {
			XA.cloneFragment(doc, frag, boxSelMgr.range);
			if (!frag.hasChildNodes()) {
				ev.preventDefault();
				return;
			}
		} else {
			//pas boxSelMgr = outline par ex.
			const node = XA.findDomLast(wedlet.wedAnchor, doc);
			if (!node || node.nodeType === ENodeType.attribute) {
				ev.preventDefault();
				return;
			}
			frag.appendChild(node.cloneNode(true));
		}
		ev.dataTransfer.effectAllowed = (wedlet as IEditableWedlet).editMode === EWedletEditMode.write ? "copyMove" : "copy";
		ev.dataTransfer.setDragImage(new Image(), 0, 0);
		(wedMgr.wedEditor as IWedEditorInsMgr).startInsertSession({originalDom: frag}, true, wedlet as IInsSessionCtrl);
	});
	root.addEventListener("dragend", function onDragEnd(this: void, ev: DragEvent) {
		const eltWedlet = DOM.findParentOrSelf(ev.target as Element, null, IS_EltWedlet);
		if (eltWedlet) (eltWedlet.wedlet.wedMgr.wedEditor as IWedEditorInsMgr).endInsertSession();
	});
}

/** Gestion des insertions au niveau des IWedlets. */
export interface IInsMgrWedlet extends IWedlet {

	markInserts(datas: OSkImportDatas): "next" | "nextSibling"

	eraseInsertMarks(): void

	/** Mémoire des datas déjà analysées. A purger en cas de changement pour forcer un appel à markInserts() au prochain cycle. */
	currentInsDatas: OSkImportDatas
}

function isInsMgrWedlet(w: IWedlet): w is IInsMgrWedlet {return w && typeof (w as IInsMgrWedlet).markInserts === 'function'}


/** Aggrégation pour les wedlet IWedletSingleElt pour gérer les marques d'insertion possibles dans ses fils. */
export function AgWedletInsMgr(cls: any): any {
	const proto = cls.prototype as IInsMgrWedlet & IInsSessionCtrl & IWedletSingleElt;
	if (!proto.markInserts) proto.markInserts = markInserts;
	if (!proto.eraseInsertMarks) proto.eraseInsertMarks = eraseInsertMarks;

	if (!proto.getDropEffect) proto.getDropEffect = function (this: IInsSessionCtrl & IWedletSingleElt, mark: MarkImportInsert, ev: DragEvent): 'copy' | 'move' | 'link' {
		const allowed = ev.dataTransfer.effectAllowed;
		if (allowed === 'copyMove') {
			let result: 'copy' | 'move' | 'link';

			function checkFrom(w: IWedlet): 'copy' | 'move' | 'link' | undefined {
				//Si on est autour de nous, on ne peut déplacer !
				if (mark.wdlBefore === w || mark.wdlAfter === w) return 'copy';
				//On ne peut déplacer dans ses propres fils.
				if (WEDLET.isAncestorOrEqual(w, mark.from)) return 'copy';
			}

			const boxSelMgr = (this.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
			if (boxSelMgr) {
				result = boxSelMgr.visitSelectedWedlets(checkFrom);
			} else {
				//pas boxSelMgr = outline par ex.
				result = checkFrom(this);
			}
			//Si ctrl enfoncé, on copie.
			return result || ACTION.isCopyOverMove(ev) ? 'copy' : 'move';
		}
		return allowed as 'copy' | 'move' | 'link';
	};

	if (!proto.finalizeInsertion) proto.finalizeInsertion = function (this: IInsSessionCtrl & IWedletSingleElt, batch: IDocBatch & XmlBatch, action?: 'copy' | 'move' | 'link'): void {
		if (action === 'move') {
			const wedMgr = this.wedMgr;
			const boxSelMgr = (wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr;
			if (batch.docHolder === wedMgr.docHolder) {
				//Même doc, on ajoute la suppr.
				batch.needAdjustForNextAdds();
				if (boxSelMgr) {
					batch.setSelBefore(boxSelMgr.range.start, boxSelMgr.range.end);
					boxSelMgr.deleteContentSelection(batch);
				} else {
					//pas boxSelMgr = outline par ex.
					batch.setSelBeforeSeq(this.wedAnchor, 1);
					batch.deleteSequence(this.wedAnchor, 1);
				}
				batch.doBatch();
			} else {
				//Docs différents, on valide l'ajout.
				batch.doBatch();
				//Puis on suppr ici.
				if (boxSelMgr) boxSelMgr.deleteContentSelection();
				else wedMgr.docHolder.newBatch().deleteSequence(this.wedAnchor, 1).doBatch();
			}
		} else {
			batch.doBatch();
		}
	};
	return cls;
}


/** Controler de la session d'insertion fourni à l'appel de IWedEditorInsMgr.startInsertSession(). */
export interface IInsSessionCtrl {

	/** Evalue l'action possible au drag. */
	getDropEffect?(mark: MarkImportInsert, ev: DragEvent): 'copy' | 'move' | 'link'

	/** Si méthode présente finalise et execute le batch (cas du move : suppr de la source). */
	finalizeInsertion?(batch: IDocBatch & XmlBatch, action?: 'copy' | 'move' | 'link'): void
}


function startInsertSession(this: IWedEditorInsMgr, datas: OSkImportDatas, byDrag: boolean, ctrl?: IInsSessionCtrl) {
	this.endInsertSession();
	this._insSession = new InsSession(this, datas, byDrag, ctrl);
}

function endInsertSession(this: IWedEditorInsMgr) {
	const insSess = this._insSession;
	if (!insSess) return;
	this._insSession = null;
	this._dragEnter = null;
	insSess.erase();
}

class InsSession {

	widgets: Set<IInsMgrWedlet> = new Set();

	constructor(public editor: IWedEditorInsMgr, public datas: OSkImportDatas, public byDrag: boolean, public ctrl: IInsSessionCtrl) {
		if (!byDrag) editor.rootNode.addEventListener('pointerdown', onPointerdownInsSession, {capture: true});
		const scrollCtn = this.editor.scrollContainer;
		if (scrollCtn) scrollCtn.addEventListener('scroll', this.onScroll, {passive: true});
		this.redraw();
	}

	/** A appeler à l'init, après scroll, après update */
	redraw() {
		//let t = performance.now();
		const wedMgr = this.editor.wedMgr;
		const rect = this.editor.rootNode.getBoundingClientRect() as DOMRectReadOnly;
		let pane = wedMgr.openedPaneWedlet;
		const cb = (wedletArea: IWedletArea) => {
			const w = wedletArea.wedlet;
			if (w === pane) return "nextSibling";
			if (isInsMgrWedlet(w) && WEDLET.isWritableWedlet(w)) {
				if (w.currentInsDatas !== this.datas) {
					this.widgets.add(w);
					return w.markInserts(this.datas);
				}
			}
			return "next";
		}
		WEDLET.findWedletInArea(cb, wedMgr.rootWedlet, rect, this.editor._insMgrVisitOptions);
		if (pane) {
			const p = pane;
			pane = null;
			WEDLET.findWedletInArea(cb, p, rect, this.editor._insMgrVisitOptions);
		}
		//console.log("InsSession-perf:::::", performance.now() - t, "widgets::", this.widgets.size);
	}

	erase() {
		//console.trace('InsSession.erase()::::::');
		if (!this.byDrag) this.editor.rootNode.removeEventListener('pointerdown', onPointerdownInsSession, {capture: true});
		if (this.editor.scrollContainer) this.editor.scrollContainer.removeEventListener('scroll', this.onScroll);
		for (const w of this.widgets) w.eraseInsertMarks();
		this.editor = null;//help gc
		this.widgets.clear();
	}

	onScroll(this: HTMLElement, ev: Event) {
		//console.log("onScroll!!!!!!");
		DOMSH.findHost<IWedEditorInsMgr>(this)._insSession.redraw()
	}

}

/** Pour les sessions qui ne sont pas activée via drag. */
async function onPointerdownInsSession(this: HTMLElement, ev: PointerEvent) {
	const eltPath = ev.composedPath();
	const editor = findWedEditor(this) as IWedEditorInsMgr;
	try {
		const mark = eltPath.find(IS_MarkImportInsert);
		if (mark) await mark.execute();
	} finally {
		editor.endInsertSession();
	}
}


/** Mark les inserts au niveau du IWedlet. */
export function markInserts(this: IInsMgrWedlet & IWedletSingleElt, datas: OSkImportDatas): "next" | "nextSibling" {
	this.currentInsDatas = datas;
	// if (this.model.nodeName === 'dk:text') {
	// 	console.log("markInserts:::::", this, datas);
	// }
	if (!isEltInsertDrawer(this.elementHost) || !WEDLET.isWritableWedlet(this)) return "next";
	if (this.wedParent == null) return "next"; //Cas particulier du wedletRoot à voir...

	const wedMgr = this.wedMgr;
	const impCtx = {xaParent: this.wedAnchor, virtualPath: WEDLET.buildVirtualPath(this)} as OSkPasteSearchContext;
	wedMgr.docHolder.house.schemaDom.tryImport(impCtx, datas)?.then((importers: ISkImporter[]) => {
		if (importers?.length > 0 && this.currentInsDatas === datas) {
			//console.log("importers:::", importers, impCtx);
			(this.elementHost as IEltInsertDrawer).drawInsertMarks(importers, datas.emptyStruct);
		}
	});
	return "next";
}

/** Efface les marks d'inserts au niveau du IWedlet. */
function eraseInsertMarks(this: IInsMgrWedlet & IWedletSingleElt) {
	this.currentInsDatas = null;
	const p = this.elementHost;
	if (isEltInsertDrawer(p) && p.isConnected) p.removeInsertMarks();
}


export interface IEltInsertDrawer extends IElementWedlet {
	drawInsertMarks(importers: ISkImporter[], emptyStruct: boolean): void

	removeInsertMarks(): void

	/** interne : marques ajoutées. */
	_insMarks: HTMLElement[];
}

function isEltInsertDrawer(elt: Element): elt is IEltInsertDrawer {return elt && ('drawInsertMarks' in elt)}

/** Aggrégation pour les IElementWedlet. */
export function AgEltBoxInsertDrawerBox(cls: any): any {
	const proto = cls.prototype as IEltInsertDrawer;
	proto.drawInsertMarks = drawInsertMarks_Box;
	proto.removeInsertMarks = removeInsertMarks;
	return cls;
}

function drawInsertMarks_Box(this: IEltInsertDrawer, importers: ISkImporter[], emptyStruct: boolean) {
	// if (this.wedlet.model.nodeName === 'op:keywds') {
	// 	console.log("drawInsertMarks", this);
	// 	//debugger;
	// }
	this._insMarks = [];
	let replaceFull: MarkImportReplace; //mémoire du replaceFull
	let replaceCh: Map<number | string, MarkImportReplaceCtn>;//mémoire des remplacements de fils.
	let insertCh: Map<number | string, MarkImportInsert>;//mémoire des insertions entre les fils.
	let replaceVirtuals: Map<IElementWedlet, MarkImportReplaceCtn>; //mémoire des remplacements des noeuds virtuels
	let orientHoriz: boolean;
	for (const imp of importers) {
		if (!imp.importPos) {
			//on remplace tout (attr liens par ex.)
			if (replaceFull) replaceFull.addImporter(imp);
			else {
				this._insMarks.push(replaceFull = new MarkImportReplace().placeOn(this.wedlet, imp, this));
			}
		} else {
			if (imp.importPos.replaceChildren) {
				//on remplace certains fils
				if (!replaceCh) replaceCh = new Map();
				repl: for (const chPath of imp.importPos.replaceChildren) {
					let mark = replaceCh ? replaceCh.get(chPath) : null;
					if (mark) {
						mark.addImporter(imp);
					} else {
						const ch = (this.wedlet as IParentWedlet).findWedletChild(chPath, WEDLET.VISITOPTIONS_mainBranch) as IWedlet;
						if (isWedletSingleElt(ch)) {
							if (!emptyStruct || !WEDLET.matchModel(imp.importPos.similarTo, ch.model)) {
								for (let widget = ch.elementHost.previousElementSibling; widget; widget = widget.previousElementSibling) {
									//Si un virtual widget précédent match le modèle à insérer, on passe.
									if (IS_EltWedlet(widget)) {
										if (widget.wedlet.model.nodeType === ENodeType.comment) continue;
										if (!widget.wedlet.isVirtual()) break;
										if (WEDLET.matchModel(imp.importPos.similarTo, widget.wedlet.model)) continue repl;
									}
								}
								mark = new MarkImportReplaceCtn().placeOn(ch, imp, ch.element);
								this._insMarks.push(mark);
								replaceCh.set(chPath, mark);
							}
						}
					}
				}
			}
			if (imp.importPos.insertOffsetMin !== undefined) {
				//on injecte des marques d'insertion
				if (!insertCh) insertCh = new Map();
				let previousCh: IWedlet;
				eachOffset: for (let i = imp.importPos.insertOffsetMin; i <= imp.importPos.insertOffsetMax; i++) {
					let mark = insertCh.get(i);
					if (mark) {
						mark.addImporter(imp);
					} else {
						const ch = (this.wedlet as IParentWedlet).findWedletChild(i, WEDLET.VISITOPTIONS_mainBranch) as IWedlet;
						//On élimine les insertions qui ont déjà une représentation virtuelle
						if (!ch || isWedletSingleElt(ch)) {
							for (let widget = ch ? (ch as IWedletSingleElt).elementHost.previousElementSibling : (this.wedlet as IWedletSingleElt).elementHost.lastElementChild; widget; widget = widget.previousElementSibling) {
								if (IS_EltWedlet(widget)) {
									if (widget.wedlet.model.nodeType === ENodeType.comment) continue;
									if (!widget.wedlet.isVirtual()) break;
									const sameModel = WEDLET.matchModel(imp.importPos.similarTo, widget.wedlet.model);
									if (emptyStruct ? !sameModel && isWedDefaultDisplay(widget.wedlet.displayCtx) : sameModel) {
										//Si on insert une struct empty : on propose de remplacer le defaultDisplay si modèle différent.
										//Si on insert un lien (ou un contenu) : on propose de remplacer si le wedlet virtuel est de même modèle.
										let mark = replaceVirtuals && replaceVirtuals.get(widget);
										if (mark) {
											mark.addImporter(imp);
										} else {
											mark = new MarkImportReplaceCtn().placeOn(widget.wedlet, imp, widget);
											this._insMarks.push(mark);
											(replaceVirtuals || (replaceVirtuals = new Map())).set(widget, mark);
										}
									}
									if (sameModel) continue eachOffset; //widget virtuel trouvé, donc pas d'autre insertion.
								}
							}
						}
						if (orientHoriz === undefined) orientHoriz = GFX.getContainerOrient((ch as IWedletSingleElt)?.elementHost?.assignedSlot || (this.wedlet as IWedletSingleElt).elementHost) === 'h';
						mark = new MarkImportInsert().placeOn(this.wedlet, imp, this, i, ch, previousCh, orientHoriz);
						this._insMarks.push(mark);
						insertCh.set(i, mark);
						previousCh = ch;
					}
				}
			}
			if (imp.importPos.insertAtts) throw "TODO imp.importPos.insertAtts";
		}
	}
}

function removeInsertMarks(this: IEltInsertDrawer) {
	if (this._insMarks) {
		for (const mark of this._insMarks) mark.remove();
		this._insMarks = null;
	}
}

/**
 * Marque d'insertion d'un contenu
 */
export class MarkImportInsert extends HTMLElement {

	from: IWedlet;
	wdlBefore: IWedlet;
	wdlAfter: IWedlet;
	offset: number;

	importer: ISkImporter | ISkImporter[];

	constructor() {
		super();
		this.ondragenter = this.onDragEnter;
		this.ondragleave = this.onDragLeave;
		this.ondragover = this.onDragOver;
		this.ondrop = this.onDrop;
		this.onkeydown = this.onKeyDown;
		this.onblur = this.onBlur;
		this.onclick = this.onClick;
		this.tabIndex = 0;
	}

	setClass(cls: string): this {
		if (cls) this.classList.add(cls);
		return this;
	}

	placeOn(from: IWedlet, importer: ISkImporter, parentRef: IElementWedlet, offset: number, before: IWedlet, after: IWedlet, orientHoriz?: boolean): this {
		this.from = from;
		this.wdlBefore = before;
		this.wdlAfter = after;
		this.offset = offset;
		this.importer = importer;
		REG.findReg(parentRef).installSkin(orientHoriz ? 'box-mark-ins/horiz' : 'box-mark-ins', this.attachShadow(DOMSH.SHADOWDOM_INIT));
		(parentRef.wedlet as IParentWedlet).injectForeignChild(importer.importPos.similarTo, this, before, after);
		return this;
	}

	addImporter(importer: ISkImporter) {
		if (!Array.isArray(this.importer)) this.importer = [this.importer, importer];
		else this.importer.push(importer);
	}

	onDragEnter(ev: DragEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setDropEffect(ev);
		DOM.addClass(this, "over");
		//console.log("MarkImportInsert.onDragEnter:::", this.nextElementSibling || this.parentElement, this.importer);
	}

	onDragOver(ev: DragEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setDropEffect(ev);
	}

	protected setDropEffect(ev: DragEvent) {
		const insCtrl = (this.from.wedMgr.wedEditor as IWedEditorInsMgr)._insSession.ctrl;
		if (insCtrl && insCtrl.getDropEffect) {
			ev.dataTransfer.dropEffect = insCtrl.getDropEffect(this, ev);
		} else {
			switch (ev.dataTransfer.effectAllowed) {
			case 'copy':
				ev.dataTransfer.dropEffect = 'copy';
				return;
			case 'move':
				ev.dataTransfer.dropEffect = 'move';
				return;
			case 'copyMove':
				if (ACTION.isCopyOverMove(ev)) {
					ev.dataTransfer.dropEffect = 'copy';
				} else {
					ev.dataTransfer.dropEffect = 'move';
				}
				return;
			}
			ev.dataTransfer.dropEffect = "link";
		}
	}

	onDragLeave(ev: DragEvent) {
		ev.preventDefault();
		//ev.stopImmediatePropagation(); NON
		DOM.removeClass(this, "over");
	}

	onDrop(ev: DragEvent) {
		this.onDragLeave(ev);
		this.setDropEffect(ev);
		this.execute(ev.dataTransfer, ev.dataTransfer.dropEffect as 'copy' | 'move' | 'link');
	}

	onKeyDown(ev: KeyboardEvent) {
		if (ev.key === 'Tab') {
			if (ev.shiftKey) findPreviousMark(this.from.wedMgr.wedEditor, this, ev);
			else findNextMark(this.from.wedMgr.wedEditor, this, ev);
		} else if (ev.key === 'ArrowDown') {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			findNextMark(this.from.wedMgr.wedEditor, this, ev);
		} else if (ev.key === 'ArrowUp') {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			findPreviousMark(this.from.wedMgr.wedEditor, this, ev);
		} else if (ev.key === 'Enter' || ev.key === ' ') {
			ev.preventDefault();
			ev.stopImmediatePropagation();
			this.execute();
		}
	}

	onBlur(ev: FocusEvent) {
		if (!this._freezeBlur) (this.from.wedMgr.wedEditor as IWedEditorInsMgr).endInsertSession();
		else this._freezeBlur = false;
	}

	_freezeBlur: boolean;

	onClick(ev: Event): void {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.execute();
	}

	async execute(dataTransfer?: DataTransfer, dropEffect?: 'copy' | 'move' | 'link') {
		const editor = this.from.wedMgr.wedEditor as IWedEditorInsMgr;
		if (Array.isArray(this.importer)) {
			const actions = [];
			for (const imp of this.importer) {
				if (dataTransfer && imp.completeDatasAtDrop) {
					if (await imp.completeDatasAtDrop(dataTransfer) === 'stop') continue;
				}
				if (imp.isAcceptable && await imp.isAcceptable() === false) continue;
				actions.push(new Action().setLabel(imp.getLabel()).setExecute(() => {this.executeImporter(imp, dropEffect)}));
			}
			switch (actions.length) {
			case 0: {
				POPUP.showNotifInfo("Import incompatible", this);
				editor.endInsertSession();
				return;
			}
			case 1: {
				return actions[0].execute(null);
			}
			default:
				const action = await POPUP.showPopupActions({actions}, {posFrom: this, fromY: 'middle', targetY: 'middle'} as ORelativePosition, editor);
				if (!action) editor.endInsertSession();
			}
		} else {
			if (dataTransfer && this.importer.completeDatasAtDrop) {
				if (await this.importer.completeDatasAtDrop(dataTransfer) === 'stop') {
					POPUP.showNotifInfo("Import incompatible", this);
					editor.endInsertSession();
					return;
				}
			}
			if (this.importer.isAcceptable && await this.importer.isAcceptable() === false) {
				POPUP.showNotifInfo("Import incompatible", this);
				editor.endInsertSession();
				return;
			}
			return this.executeImporter(this.importer, dropEffect);
		}
	}

	async executeImporter(imp: ISkImporter, action: 'copy' | 'move' | 'link') {
		//console.log("executeImporter:::", imp);
		const wedMgr = this.from.wedMgr;
		let insCtrl: IInsSessionCtrl;
		const batch = wedMgr.docHolder.newBatch();
		try {
			insCtrl = (wedMgr.wedEditor as IWedEditorInsMgr)._insSession.ctrl;
			if (imp.needAsyncBuild) {
				if (await imp.buildContentToImport(wedMgr.wedEditor) === 'stop') return;
			}
			imp.doImport(this.buildImportContext(batch), batch);
		} finally {
			(wedMgr.wedEditor as IWedEditorInsMgr).endInsertSession();
		}
		if (insCtrl?.finalizeInsertion) insCtrl.finalizeInsertion(batch, action);
		else batch.doBatch();
	}

	protected buildImportContext(batch: IDocBatch & XmlBatch): OSkPasteContext {
		if (this.from.isVirtual()) {
			return {sel: {start: XA.append(this.from.wedAnchor, 0)}, virtualPath: WEDLET.buildVirtualPath(this.from)}
		} else {
			return {sel: {start: XA.append(this.from.wedAnchor, this.offset)}};
		}
	}
}

REG.reg.registerSkin('box-mark-ins', 1, /* language=CSS */ `
	:host {
		display: block;
		position: relative;
		z-index: 900;
		width: 38px;
		height: 16px;
		margin-top: -8px;
		margin-bottom: -8px;
		box-sizing: border-box;
		background-color: var(--edit-drop-color);
		background-clip: padding-box;
		border: 6px solid transparent;
		border-radius: 12px;
	}

	:host(.small) {
		border: 7px solid transparent;
	}

	:host(.over),
	:host(:hover),
	:host(:focus) {
		width: 48px;
		height: 20px;
		margin-top: -10px;
		margin-bottom: -10px;
		border: 7px solid var(--edit-drop-bgcolor);
		border-radius: 14px;
		margin-inline-start: -1px;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

REG.reg.registerSkin('box-mark-ins/horiz', 1, /* language=CSS */ `
	:host {
		display: block;
		position: relative;
		z-index: 900;
		width: 12px;
		height: 2em;
		margin-inline-start: -6px;
		margin-inline-end: -6px;
		box-sizing: border-box;
		background-color: var(--edit-drop-color);
		background-clip: padding-box;
		border: 4px solid transparent;
		border-bottom-width: .3em;
		border-top-width: .3em;
		border-radius: 8px;
	}

	:host(.over),
	:host(:hover),
	:host(:focus) {
		width: 16px;
		margin-inline-start: -8px;
		margin-inline-end: -8px;
		border: 5px solid var(--edit-drop-bgcolor);
		border-bottom-width: .3em;
		border-top-width: .3em;
		border-radius: 10px;
		margin-block-start: -1px;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

customElements.define('box-mark-ins', MarkImportInsert);

function IS_MarkImportInsert(n: Node): n is MarkImportInsert {return n instanceof MarkImportInsert}

function findPreviousMark(wedEditor: IWedEditor, mark: MarkImportInsert, ev: KeyboardEvent) {
	const prev = DOMSH.findFlatPrevious(mark || wedEditor.rootNode, wedEditor.rootNode, IS_MarkImportInsert);
	if (prev) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		mark._freezeBlur = true;
		prev.focus();
	} else if (wedEditor.scrollContainer) {
		wedEditor.scrollContainer.scrollBy({top: -wedEditor.scrollContainer.clientHeight * .8, behavior: "smooth"});
	}
}

function findNextMark(wedEditor: IWedEditor, mark?: MarkImportInsert, ev?: KeyboardEvent) {
	const next = DOMSH.findFlatNext(mark || wedEditor.rootNode, wedEditor.rootNode, IS_MarkImportInsert);
	//console.log("findNextMark::::::", next);
	if (next) {
		if (ev) {
			ev.preventDefault();
			ev.stopImmediatePropagation();
		}
		if (mark) mark._freezeBlur = true;
		next.focus();
	} else if (wedEditor.scrollContainer) {
		//Plus de suivants, on opère un scroll correspondant à un changement de pages pour découvrir de nouvelles marks
		wedEditor.scrollContainer.scrollBy({top: wedEditor.scrollContainer.clientHeight * .8, behavior: "smooth"});
	}
}

function findVisibleMark(wedEditor: IWedEditor) {
	const top = wedEditor.scrollContainer ? wedEditor.scrollContainer.scrollTop : 0;
	let next = DOMSH.findFlatNext(wedEditor.rootNode, wedEditor.rootNode, IS_MarkImportInsert);
	while (next) {
		if (next.offsetTop + next.offsetHeight > top) {
			next.focus();
			return;
		}
		next = DOMSH.findFlatNext(next, wedEditor.rootNode, IS_MarkImportInsert);
	}
}

/**
 * Marque de remplacement du contenu
 */
export class MarkImportReplace extends MarkImportInsert {
	placeOn(from: IWedlet, importer: ISkImporter, ref: HTMLElement): this {
		this.from = from;
		this.importer = importer;
		REG.findReg(ref).installSkin(this.localName, this.attachShadow(DOMSH.SHADOWDOM_INIT));
		ref.style.setProperty('position', 'relative');
		(ref.shadowRoot || ref).appendChild(this);
		return this;
	}

	protected buildImportContext(): OSkPasteContext {
		const xa = this.from.wedAnchor;
		if (XA.isAttribute(xa)) {
			return this.from.isVirtual() ? {sel: {start: xa}, virtualPath: WEDLET.buildVirtualPath(this.from)} : {sel: {start: xa}};
		} else if (this.from.isVirtual()) {
			return {sel: {start: xa}, virtualPath: WEDLET.buildVirtualPath(this.from.wedParent)}
		} else {
			return {sel: XA.newRangeAround(xa)}
		}
	}
}

REG.reg.registerSkin('box-mark-rep', 1, /* language=CSS */ `
	:host {
		background-color: var(--edit-drop-bgcolor);
		position: absolute;
		z-index: 900;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
	}

	:host(.over),
	:host(:hover),
	:host(:focus) {
		background-color: var(--edit-drop-bgcolor);
		outline: 2px solid var(--edit-drop-color);
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

customElements.define('box-mark-rep', MarkImportReplace);


/**
 * Marque de remplacement d'un container
 */
export class MarkImportReplaceCtn extends MarkImportReplace {
	placeOn(from: IWedlet, importer: ISkImporter, ref: HTMLElement): this {
		this.from = from;
		this.importer = importer;
		REG.findReg(ref).installSkin(this.localName, this.attachShadow(DOMSH.SHADOWDOM_INIT));
		ref.style.setProperty('position', 'relative');
		(ref.shadowRoot || ref).appendChild(this);
		return this;
	}

	onDragEnter(ev: DragEvent) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		this.setDropEffect(ev);
		if (DOM.addClass(this, "over")) {
			const prev = this.previousElementSibling;
			if (!(prev?.hasAttribute("boxmarkrepctn-over"))) {
				this.parentNode.insertBefore(<div boxmarkrepctn-over="" style="pointer-events: none; background-color: var(--edit-drop-bgcolor); position: absolute;	top:0; bottom:0; left:0; right:0; z-index: 1;"/>, this);
			}
		}
		//console.log("MarkImportReplaceCtn.onDragEnter:::", this.parentElement || DOMSH.findHost(this), this.importer);
	}

	onDragLeave(ev: DragEvent) {
		ev.preventDefault();
		//ev.stopImmediatePropagation(); NON
		if (DOM.removeClass(this, "over")) {
			const prev = this.previousElementSibling;
			if (prev?.hasAttribute("boxmarkrepctn-over")) prev.remove();
		}
	}
}

REG.reg.registerSkin('box-mark-rep-ctn', 1, /* language=CSS */ `
	:host {
		background: linear-gradient(to right, var(--edit-drop-bgcolor), var(--edit-drop-bgcolor), transparent);
		position: absolute;
		z-index: 900;
		top: 0;
		bottom: 0;
		left: 0;
		width: 15px;
	}

	:host(:hover),
	:host(:focus) {
		background: linear-gradient(to right, var(--edit-drop-bgcolor), var(--edit-drop-bgcolor), transparent);
		width: 30px;
	}

	:host(:focus) {
		outline: var(--focus-outline);
	}
`);

customElements.define('box-mark-rep-ctn', MarkImportReplaceCtn);

/** Affichage d'un popup des structs insérables dans l'écran courant. */
class ListInsBoxStruct extends Action<IWedEditorBoxSel & IWedEditorInsMgr> {

	constructor() {
		super("insTag");
		this._label = "Insérer...";
		this._icon = "/@skin@/edit/wed/insertTag.svg";
	}

	isVisible(ctx: IWedEditorBoxSel & IWedEditorInsMgr): boolean {
		if (!ctx.boxSelMgr || (ctx.config as OWedEditorBoxConfig).disableBoxSelection) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IWedEditorBoxSel & IWedEditorInsMgr): boolean {
		if (ctx.wedMgr.readOnly) return false;
		return super.isEnabled(ctx);
	}

	execute(ctx: IWedEditorBoxSel & IWedEditorInsMgr, ev?: Event): any | "noPreventDefault" {
		//console.log("InsBoxStruct!!!!!!!!!");
		ctx.boxSelMgr.clearSel();
		const structs = new Set<ISkStructDef>(); //XXX Map<ISkStructDef, ParentWedlet> pour construction graphique plus riche de la struct, dédoublonnage du label... ?
		const virtualParents = new Set(); // cache des parents virtuels déjà traités.
		WEDLET.findWedletInArea((wedletArea: IWedletArea): 'next' | 'nextSibling' | 'stop' => {
			const w = wedletArea.wedlet;
			const wParent = w.wedParent;
			if (wParent && isWedletBoxSelection(w) && isWedletSingleElt(w) && WEDLET.isWritableWedlet(wParent) && !(wParent as IWedletSingleElt).isCollapsed) {
				//On limite la recherche des structs insérables aux frères des wedlets isWedletBoxSelection()
				// pour sauter les niveaux de profondeur du flux XML qui sont graphiquement masqués et non manipulables.
				const xaPart = w.xaPart;
				const wedMgr = ctx.wedMgr;
				if (wParent.isVirtual()) {
					//on est dans un contexte virtuel
					if (!virtualParents.has(wParent)) {
						virtualParents.add(wParent);
						//parent pas encore évalué, et un fils virtuel est bien isWedletBoxSelection, c'est donc un niveau d'insertion
						const struct = wedMgr.docHolder.getStruct(wParent.wedAnchor, WEDLET.buildVirtualPath(wParent));
						if (struct instanceof SkRuleNode) {
							const subStructs = struct.contentRule.findRules((r) => r.structType == ENodeType.element);
							if (subStructs) {
								WEDLET.cleanupStructList(subStructs, WEDLET.findVirtualWedletsAfter(w.element, WEDLET.findVirtualWedletsBefore(w.element, true)), true);
								//console.log("getInsertableStructs inVirtuals:::", wParent, subStructs);
								for (const s of subStructs) if (s) structs.add(s);
							}
						}
					}
				} else {
					if (w.isVirtual()) {
						//wedlet virtuel
						if (w.model.nodeType === ENodeType.element) {
							const [structsBefore] = wedMgr.docHolder.getInsertableStructs(wParent.wedAnchor, w.getVirtualXaPart() as number);
							WEDLET.cleanupStructList(structsBefore, WEDLET.findVirtualWedletsAfter(w.element, WEDLET.findVirtualWedletsBefore(w.element, true)), true);
							//console.log("getInsertableStructs:::", structsBefore);
							if (structsBefore) for (const s of structsBefore) if (s?.structType === ENodeType.element) structs.add(s);
							if (w.model.config.hasAttribute("insertUiForChildren")) {
								const struct = wedMgr.docHolder.getStruct(w.wedAnchor, WEDLET.buildVirtualPath(w)) as SkRuleNode;
								//todo suppr virtuals affichés, restrictions au même childrenElt comme ci-après ?
								struct?.contentRule?.findRules((r) => r instanceof SkRuleNode)?.forEach((s) => {if (s?.structType === ENodeType.element) structs.add(s)});
							}
						}
					} else if (typeof xaPart === 'number') {
						//Ce n'est pas un attribut
						//FIXME PERF double test de xaPart et xaPart + 1 pour chaque frère avec son frère suivant.
						const [structsBefore, structsAfter, structsReplace] = wedMgr.docHolder.getInsertableStructs(wParent.wedAnchor, xaPart, xaPart + 1, false, xaPart);
						WEDLET.cleanupStructList(structsBefore, WEDLET.findVirtualWedletsBefore(w.element), true);
						WEDLET.cleanupStructList(structsAfter, WEDLET.findVirtualWedletsAfter(w.element), true);
						const childrenElt = w.element.fromChildrenElt;

						//console.log("getInsertableStructs:::", structsBefore, structsAfter, structsReplace);
						function appendStructs(structList: ISkStructDef[]) {
							if (structList) for (const s of structList) if (s?.structType === ENodeType.element) {
								if (!childrenElt || !childrenElt.wedMatchStrict || childrenElt.wedMatcher.matchStruct(s)) structs.add(s);
							}
						}

						appendStructs(structsBefore);
						appendStructs(structsReplace);
						appendStructs(structsAfter);

						if (w.model.config.hasAttribute("insertUiForChildren")) {
							const subStructs = wedMgr.docHolder.getInsertableStructsAll('children', w.wedAnchor);
							if (WEDLET.cleanupStructList(subStructs[0], WEDLET.findAllChildrenVirtualWedlets(w.element)) > 0) appendStructs(subStructs[0]);
						}
					}
				}
			}
			return 'next';
		}, ctx.wedMgr.rootWedlet, ctx.rootNode.getBoundingClientRect() as DOMRectReadOnly, WEDLET.VISITOPTIONS_includeVirtuals);
		//console.log("ListInsBoxStruct:::", structs);
		if (structs.size > 0) {
			const rootList = <div id="list"/> as HTMLElement;
			const structsAdded = [] as SkRuleElt[];
			list:for (const s of structs) {
				for (const other of structsAdded) if (other.isSameRoot(s as SkRuleElt)) continue list;
				structsAdded.push(s as SkRuleElt);
				rootList.appendChild(ActionBtn.buildButton(new InsertableStruct(s), ctx, 'custom'));
			}

			const rootFilter = <div id="search">
				<input type="search" oninput={this.onSearchChange} onchange={this.onSearchChange} onkeydown={this.onSearchKeydown} onfocus={() => {ctx.endInsertSession()}}/>
			</div> as HTMLElement;

			const root = <div>
				<ShadowJsx reg={ctx.wedMgr.reg} skin="ListInsBoxStruct" skinOver="webzone:panel">
					{rootFilter}
					{rootList}
				</ShadowJsx>
			</div>;

			const popup = POPUP.showFloating(root, listInsPopupPos(ctx.rootNode), ctx);

			root.addEventListener('focusout', function (this: HTMLDivElement, ev: FocusEvent) {
				if (!ev.relatedTarget || !DOM.isAncestor(this, ev.relatedTarget as Node)) {
					ctx.endInsertSession();
					popup.close();
				}
			});
			rootList.addEventListener('focusin', function (this: HTMLDivElement, ev: FocusEvent) {
				const target = ev.target;
				if (IS_ActionableEnabled(target)) (target.action as InsertableStruct).startSession(target.actionContext, false);
			});
			rootList.addEventListener('pointerover', function (this: IPopupable, ev: MouseEvent) {
				const target = ev.target;
				if (IS_ActionableEnabled(target)) target.focus();
			});
			rootList.addEventListener('keydown', function (this: IPopupable, ev: KeyboardEvent) {
				let next: HTMLElement;
				if (ev.key === 'ArrowDown') {
					next = DOM.findNextSibling(ev.target as Element, IS_ActionableEnabled);
				} else if (ev.key === 'ArrowUp') {
					next = DOM.findPreviousSibling(ev.target as Element, IS_ActionableEnabled);
				} else return;
				const target = ev.target;
				if (next) {
					next.focus();
					ev.preventDefault();
					ev.stopImmediatePropagation();
				}
			});
			DOM.findNext(root.shadowRoot, null, DOM.IS_focusable)?.focus();
		} else {
			POPUP.showFloating(<div style="padding:.5em; font-style:italic;">
				<ShadowJsx reg={ctx.wedMgr.reg} skin="webzone:panel">
					{"Aucune insertion possible"}
				</ShadowJsx>
			</div>, listInsPopupPos(ctx.rootNode), ctx, {closeOnBlur: true});
		}
	}

	protected onSearchChange(this: HTMLInputElement, ev: Event) {
		const value = this.value.trim();
		const IS_actionBtn = (n: Node): n is ActionBtn<any> => n instanceof ActionBtn;
		let btn = DOM.findFirstChild(DOMSH.findDocumentOrShadowRoot(this).getElementById("list"), IS_actionBtn);
		if (value) {
			const regexp = new RegExp(LANG.escape4RegexpFuzzy(value), 'i');
			while (btn != null) {
				btn.hidden = !regexp.test(btn.label);
				btn = DOM.findNextSibling(btn, IS_actionBtn);
			}
		} else {
			while (btn != null) {
				btn.hidden = false;
				btn = DOM.findNextSibling(btn, IS_actionBtn);
			}
		}
	}

	onSearchKeydown(this: HTMLInputElement, ev: KeyboardEvent) {
		if (ev.key === "ArrowDown") {
			DOM.findFirstChild(DOMSH.findDocumentOrShadowRoot(this).getElementById("list"), DOM.IS_focusable)?.focus();
		}
	}
}

function listInsPopupPos(posFrom: HTMLElement): ORelativePosition {
	return {
		posFrom: posFrom,
		fromX: "start",
		targetX: "start",
		marginX: 200,
		toleranceX: 50,
		fromY: "middle",
		targetY: "middle",
		toleranceY: 10000,
		notAvailableSpace: {
			posFrom: posFrom,
			fromX: "end",
			targetX: "start",
			toleranceX: 10000,
			fromY: "middle",
			targetY: "middle",
			toleranceY: 10000,
		}
	};
}

REG.reg.registerSkin('ListInsBoxStruct', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	#search {
		display: flex;
		flex-direction: column;
		background: 0.1em / 1em no-repeat url(/@skin@/commons/icons/filter.svg) var(--form-search-bgcolor);
		padding-inline-start: 1.2em;
		border-bottom: 1px solid var(--border-color);
	}

	input {
		flex: 1;
		padding: 2px;
		background: var(--form-search-bgcolor);
		color: var(--form-color);
		border: none;
		font-size: inherit;
	}

	:focus {
		outline: var(--edit-input-focus);
	}

	#list {
		display: flex;
		flex-direction: column;
	}

	c-action {
		align-items: start;
		text-align: start;
		padding: 1px 2px;
	}

	c-action:focus {
		background-color: var(--row-inSel-bgcolor);
		outline: none;
	}
`);

/**
 * Définition d'une structure insérable.
 * Utilisé dans le contexte de ListInsBoxStruct.
 */
class InsertableStruct extends Action<IWedEditorBoxSel & IWedEditorInsMgr> {
	constructor(public struct: ISkStructDef) {
		super(struct.structName);
		this._label = struct.structLabel;
	}

	execute(ctx: IWedEditorBoxSel & IWedEditorInsMgr, ev?: Event): any | "noPreventDefault" {
		const isDrag = ev instanceof DragEvent;
		if (!isDrag) {
			const focus = DOMSH.findDeepActiveElement();
			if (focus instanceof HTMLElement) focus.blur();
			POPUP.findPopupableParent(ev.target as Element).close();
		}
		this.startSession(ctx, isDrag);
		setTimeout(() => {findVisibleMark(ctx)}, 200);
	}

	startSession(ctx: IWedEditorBoxSel & IWedEditorInsMgr, byDrag: boolean) {
		const ct = [] as IJmlSet;
		const root = DOM.newDomDoc().createElementNS(DOM.SCCORE_NS, DOM.SCFRAGMENT_TAG);
		this.struct.createContent(ct);
		ctx.startInsertSession({
			originalDom: JML.jmlToDom(ct, root),
			emptyStruct: true
		}, byDrag);
	}

}
